import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Popover,
  Checkbox,
  createFilterOptions,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as AddIcon } from '../../../../assets/common-assets/Add.svg';
import {
  AddTagButtonContainer,
  AddTagIconBox,
  AddTagPopupContainer,
  ChipStyled,
  SelectTagAutoComplete,
  TagNameTypography,
  classes,
  EditIconStyle,
  DeleteIconStyle,
  AutocompleteListBox,
  AutocompleteEditDeleteIconBox,
} from './utils';
import tag from '../../../../assets/allInvoice-assets/tag.svg';
import {
  AssociateTag,
  RemoveAssociateTag,
  deleteTag,
  getTagData,
  renameTag,
} from '../../../../services/tagApi';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import CustomTooltip from '../../utils/CustomTooltip';
import Toast from '../../utils/Toast';
import { DOCUMENT_ACTION_TOOLTIP } from '../../../../constants/tooltipConstant';
import ConfirmDialog from '../../utils/ConfirmDialog';
import { ReactComponent as editIcon } from '../../../../assets/common-assets/edit-2.svg';
import { ReactComponent as deleteIcon } from '../../../../assets/common-assets/trash.svg';
import MODELS from '../../../../constants/models';

const AddTag = ({
  id,
  documentTag,
  upgradeGrid,
  onApiCompletion,
  documentType,
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const [anchorEl, setAnchorEl] = useState(null);
  const [tags, setTags] = useState([]);
  const [value, setValue] = useState([]);
  const [tagNames, setTagNames] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [loggedTags, setLoggedTags] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [changesMade, setChangesMade] = useState(false);
  const [previousTagNames, setPreviousTagNames] = useState([]);
  const [createdTagName, setCreatedTagName] = useState([]);
  const accessToken = getLocalStorageItem('AccessToken');
  const [actionEl, setActionEl] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [askingMessage, setAskingMessage] = useState('');
  const [editTag, setEditTag] = useState(false);
  const [isDeleteTag, setIsDeleteTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagError, setTagError] = useState('');

  const navigate = useNavigate();

  let getTags;
  if (accessToken) {
    getTags = async (nestedDocumentTag) => {
      const res = await getTagData();
      if (res) {
        setTags(res);
        if (nestedDocumentTag.length > 0) {
          setValue(nestedDocumentTag);
          setPreviousTagNames(nestedDocumentTag);
        }
        setIsLoading(false);
      }
    };
  }

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (changesMade) {
      upgradeGrid(value);
    }
  };

  useEffect(() => {
    if (!code) {
      if (anchorEl !== null) {
        getTags(documentTag);
        let route;
        switch (documentType) {
          case MODELS.INVOICE:
            route = 'invoices';
            break;
          case MODELS.SALES:
            route = 'sales';
            break;
          case MODELS.RECEIPTS:
            route = 'receipts';
            break;
          case MODELS.CREDIT_CARD_STATEMENT:
            route = 'creditcardstatements';
            break;
          case MODELS.BANK_STATEMENT:
            route = 'bank-statements';
            break;
          default:
            route = 'invoices'; // You can handle unknown cases with a default route
        }
        navigate(`/${route}/${id}`);
      } else if (documentType === MODELS.INVOICE) {
        navigate('/invoices');
      } else if (documentType === MODELS.RECEIPT) {
        navigate('/receipts');
      } else if (documentType === MODELS.SALES) {
        navigate('/sales');
      } else if (documentType === MODELS.CREDIT_CARD_STATEMENT) {
        navigate('/creditcardstatements');
      } else if (documentType === MODELS.BANK_STATEMENT) {
        navigate('/bank-statements');
      }
    }
  }, [open, anchorEl, code]);

  const handleAddValue = (newValue) => {
    setChangesMade(true);
    const selectedValues = [];
    if (
      newValue.some(
        (item) =>
          item.inputValue &&
          !tags.some(
            (tagname) =>
              tagname.name === item.inputValue &&
              !selectedValues.includes(item.inputValue),
          ),
      )
    ) {
      const filteredValue = newValue.filter(
        (item) =>
          item.inputValue &&
          !tags.some((tagname) => tagname.name === item.inputValue) &&
          !selectedValues.includes(item.inputValue),
      );
      selectedValues.push(...filteredValue.map((item) => item.inputValue));
      setCreatedTagName(filteredValue);
    } else {
      setCreatedTagName([]);
    }

    setValue(newValue);

    if (newValue.length > 0) {
      const currentlySelectedTag = newValue[newValue.length - 1];
      setSelectedTagId(currentlySelectedTag.id);
    } else {
      setSelectedTagId('');
    }
  };

  useEffect(() => {
    createdTagName.forEach((item) => {
      if (item.inputValue) {
        const newCreatedTag = item.inputValue;
        if (!loggedTags.includes(item.inputValue)) {
          setLoggedTags(newCreatedTag);
          setSelectedTagId('');
        }
      }
    });
  }, [createdTagName, loggedTags]);

  const AssociateNewTag = async (documentId, tagId, tagname, callback) => {
    try {
      const associateTag = await AssociateTag(
        documentId,
        tagId,
        tagname,
        callback,
      );
      callback(associateTag);
      return associateTag;
    } catch (error) {
      Toast({ message: `${error?.message}`, variant: 'error' });
    }
  };

  useEffect(() => {
    if (
      typeof selectedTagId !== 'undefined' &&
      (selectedTagId !== '' || loggedTags !== '')
    ) {
      AssociateNewTag(id, selectedTagId, loggedTags, () => {
        if (loggedTags !== '') {
          getTags(value);
        }
        setCreatedTagName([]);
        setLoggedTags('');
      });
    }
  }, [selectedTagId, loggedTags, documentTag]);

  useEffect(() => {
    const updatedTagNames = value.map((item) => {
      if (item.inputValue) {
        const matchingTags = tags.find(
          (newTags) => newTags.name === item.inputValue,
        );
        return matchingTags ? matchingTags.id : '';
      }
      const matchingTag = tags.find((newTags) => newTags.name === item.name);
      return matchingTag ? matchingTag.id : '';
    });

    setTagNames(updatedTagNames);
  }, [value, tags]);

  const DeleteAssociateTag = async (documentId, tagId) => {
    try {
      return await RemoveAssociateTag(documentId, tagId);
    } catch (error) {
      Toast({ message: `${error?.message}`, variant: 'error' });
    }
  };

  useEffect(() => {
    if (anchorEl !== null && tagNames.length < previousTagNames.length) {
      DeleteAssociateTag(id, tagNames);
    }
    setPreviousTagNames(tagNames);
  }, [id, tagNames]);

  const filter = createFilterOptions();

  const handleMenuOpen = (event, option) => {
    setActionEl(event.currentTarget);
    setSelectedOption(option);
  };

  const handleMenuClose = () => {
    setActionEl(null);
  };

  const handleEditIcon = () => {
    setIsDialogOpen(true);
    setEditTag(true);
    setNewTag(selectedOption.name);
    setIsDeleteTag(false);
    setAskingMessage('Are You Sure You Want To Rename This Tag ?');
  };

  const handleDeleteIcon = () => {
    setIsDialogOpen(true);
    setEditTag(false);
    setIsDeleteTag(true);
    setAskingMessage('Are You Sure You Want To Delete This Tag ?');
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setActionEl(null);
  };

  const handleConfirmAction = async () => {
    if (tagError) {
      setTagError('Tag cannot be empty');
    } else if (newTag && newTag !== selectedOption?.name) {
      const response = await renameTag(selectedOption?.id, newTag);
      if (response) {
        onApiCompletion();
      }
    } else if (isDeleteTag) {
      const response = await deleteTag(selectedOption?.id);
      if (response) {
        onApiCompletion();
      }
    }
  };

  return (
    <Box className={classes.addTagBox}>
      <Box className={open ? 'action' : 'my-icon'}>
        <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.ASSIGN_TAG}>
          <Box
            component="img"
            onClick={handleClick}
            src={tag}
            alt="tag"
            className={classes.actionIcons}
            style={{
              height: '15px',
              width: '15px',
            }}
          />
        </CustomTooltip>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            className: 'pop-over-class',
          },
        }}
      >
        <AddTagPopupContainer>
          <SelectTagAutoComplete
            multiple
            limitTags={2}
            open
            disablePortal
            ListboxProps={{ className: 'Autocomplete_scrollbar' }}
            value={value}
            isOptionEqualToValue={(option, newValue) => {
              if (newValue.inputValue) {
                return option.name === newValue.inputValue;
              }
              return option.name === newValue.name;
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const modifiedEvent = { ...event };
                modifiedEvent.defaultMuiPrevented = true;
                handleClose();
              }
            }}
            onChange={(event, newValue) => {
              handleAddValue(newValue);
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);

              const { inputValue } = params;
              const isExisting = options.some(
                (option) => inputValue === option.name,
              );
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  name: `Add "${inputValue}"`,
                });
              }

              return filtered;
            }}
            disableCloseOnSelect
            freeSolo
            options={tags}
            getOptionLabel={(option) => {
              // Value selected with enter, right from the input
              if (typeof option === 'string') {
                return option;
              }
              // Add "xxx" option created dynamically
              if (option.inputValue) {
                return option.inputValue;
              }
              // Regular option
              return option.name;
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                {option.inputValue ? (
                  <AddTagButtonContainer>
                    <AddTagIconBox>
                      <AddIcon />
                    </AddTagIconBox>
                    Add &ldquo;
                    {option.inputValue}
                    &rdquo;
                  </AddTagButtonContainer>
                ) : (
                  <AutocompleteListBox>
                    <Checkbox checked={selected} />
                    <AutocompleteEditDeleteIconBox>
                      <TagNameTypography variant="body1">
                        {option.name}
                      </TagNameTypography>
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleMenuOpen(event, option);
                        }}
                      >
                        <MoreHorizIcon className="more-vert-icon" />
                      </IconButton>
                    </AutocompleteEditDeleteIconBox>
                  </AutocompleteListBox>
                )}
              </li>
            )}
            renderTags={(tagValues, getTagProps) =>
              tagValues.map((option, index) => (
                <ChipStyled
                  key={`${index + 1}`}
                  label={option.inputValue ? option.inputValue : option.name}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                autoFocus
                {...params}
                placeholder={isLoading ? 'Loading...' : 'Search Tags'}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </AddTagPopupContainer>
      </Popover>
      <Menu
        anchorEl={actionEl}
        open={Boolean(actionEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditIcon}>
          <EditIconStyle component={editIcon} />
          <Typography variant="body1">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleDeleteIcon}>
          <DeleteIconStyle component={deleteIcon} />
          <Typography variant="body1">Delete</Typography>
        </MenuItem>
      </Menu>

      {isDialogOpen ? (
        <ConfirmDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmAction}
          askingMessage={askingMessage}
          editTag={editTag}
          newTag={newTag}
          setNewTag={setNewTag}
          tagError={tagError}
          setTagError={setTagError}
          setActionEl={setActionEl}
        />
      ) : null}
    </Box>
  );
};

AddTag.defaultProps = {
  id: null,
};

AddTag.propTypes = {
  id: PropTypes.string,
  documentTag: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  upgradeGrid: PropTypes.func.isRequired,
  onApiCompletion: PropTypes.func.isRequired,
  documentType: PropTypes.string.isRequired,
};

export default AddTag;
